import { useState, useEffect } from 'react';
import { Clock, TrendingUp, Users, Calendar, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOILDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [adjustmentHours, setAdjustmentHours] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');

  useEffect(() => {
    fetchEmployeesWithTOIL();
  }, []);

  const fetchEmployeesWithTOIL = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        // Filter to show only Warehouse department employees, active employees, and sort by TOIL balance (highest first)
        const warehouseEmployees = data
          .filter(emp => emp.active !== false && emp.department === 'Warehouse')
          .map(emp => ({
            ...emp,
            toil_balance: emp.toil_balance || 0
          }))
          .sort((a, b) => b.toil_balance - a.toil_balance);
        
        setEmployees(warehouseEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageTOIL = async () => {
    if (!selectedEmployee || !adjustmentHours || !adjustmentReason) {
      toast.error('Please fill in all fields');
      return;
    }

    const hours = parseFloat(adjustmentHours);
    if (isNaN(hours) || hours <= 0) {
      toast.error('Please enter a valid number of hours');
      return;
    }

    const adjustment = adjustmentType === 'add' ? hours : -hours;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/employees/${selectedEmployee.id}/adjust-toil`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adjustment,
          reason: adjustmentReason
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`TOIL ${adjustmentType === 'add' ? 'added' : 'deducted'} successfully. New balance: ${data.new_balance.toFixed(1)}h`);
        setShowManageDialog(false);
        setSelectedEmployee(null);
        setAdjustmentHours('');
        setAdjustmentReason('');
        fetchEmployeesWithTOIL();
      } else {
        const error = await res.json().catch(() => ({ detail: 'Failed to adjust TOIL' }));
        toast.error(error.detail || 'Failed to adjust TOIL');
      }
    } catch (error) {
      console.error('Error adjusting TOIL:', error);
      toast.error('Failed to adjust TOIL');
    }
  };

  const employeesWithTOIL = employees.filter(emp => emp.toil_balance > 0);
  const totalTOIL = employees.reduce((sum, emp) => sum + emp.toil_balance, 0);
  const averageTOIL = employees.length > 0 
    ? totalTOIL / employees.length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">TOIL Overview</h1>
          <p className="text-gray-600 mt-1">Time Off In Lieu balances for Warehouse employees</p>
        </div>
        <Button
          onClick={() => setShowManageDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          Manage TOIL
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total TOIL Hours</p>
              <p className="text-2xl font-bold text-gray-900">{totalTOIL.toFixed(1)}h</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-3 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Employees with TOIL</p>
              <p className="text-2xl font-bold text-gray-900">{employeesWithTOIL.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Average TOIL</p>
              <p className="text-2xl font-bold text-gray-900">{averageTOIL.toFixed(1)}h</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Highest Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {employeesWithTOIL.length > 0 ? employeesWithTOIL[0].toil_balance.toFixed(1) : '0.0'}h
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* TOIL Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job Title
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TOIL Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No warehouse employees found
                  </td>
                </tr>
              ) : (
                employees.map((emp) => (
                  <tr 
                    key={emp.id} 
                    className={`hover:bg-gray-50 ${emp.toil_balance === 0 ? 'opacity-50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-700 font-semibold text-sm">
                            {emp.first_name?.[0]}{emp.last_name?.[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {emp.first_name} {emp.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {emp.department || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {emp.job_title || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-lg font-bold ${
                        emp.toil_balance > 0 
                          ? 'text-green-600' 
                          : 'text-gray-400'
                      }`}>
                        {emp.toil_balance.toFixed(1)}h
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {emp.toil_balance > 0 ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Has TOIL
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">
                          No TOIL
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {employees.length} warehouse employee{employees.length !== 1 ? 's' : ''}
            {employeesWithTOIL.length > 0 && (
              <span className="ml-2 text-green-600 font-semibold">
                • {employeesWithTOIL.length} with TOIL balance
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Manage TOIL Dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage TOIL Balance</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Select Employee (Warehouse Only)</Label>
              <select
                value={selectedEmployee?.id || ''}
                onChange={(e) => {
                  const emp = employees.find(emp => emp.id === e.target.value);
                  setSelectedEmployee(emp);
                }}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">-- Select a warehouse employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            {selectedEmployee && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-900">Current TOIL Balance</p>
                <p className="text-2xl font-bold text-blue-600">{selectedEmployee.toil_balance.toFixed(1)} hours</p>
              </div>
            )}

            <div>
              <Label>Action</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={adjustmentType === 'add' ? 'default' : 'outline'}
                  onClick={() => setAdjustmentType('add')}
                  className={adjustmentType === 'add' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'flex-1'}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add TOIL
                </Button>
                <Button
                  type="button"
                  variant={adjustmentType === 'remove' ? 'default' : 'outline'}
                  onClick={() => setAdjustmentType('remove')}
                  className={adjustmentType === 'remove' ? 'bg-red-600 hover:bg-red-700 flex-1' : 'flex-1'}
                >
                  <Minus className="w-4 h-4 mr-2" />
                  Deduct TOIL
                </Button>
              </div>
            </div>

            <div>
              <Label>Hours</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={adjustmentHours}
                onChange={(e) => setAdjustmentHours(e.target.value)}
                placeholder="Enter hours (e.g., 8 or 8.5)"
              />
            </div>

            <div>
              <Label>Reason</Label>
              <Textarea
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="Enter reason for TOIL adjustment (e.g., 'Data migration from old system')"
                rows={3}
              />
            </div>

            {selectedEmployee && adjustmentHours && (
              <div className={`p-3 rounded-lg border ${adjustmentType === 'add' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <p className="text-sm font-medium">New Balance Preview:</p>
                <p className="text-lg font-bold">
                  {adjustmentType === 'add' 
                    ? (selectedEmployee.toil_balance + parseFloat(adjustmentHours || 0)).toFixed(1)
                    : (selectedEmployee.toil_balance - parseFloat(adjustmentHours || 0)).toFixed(1)
                  } hours
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowManageDialog(false);
              setSelectedEmployee(null);
              setAdjustmentHours('');
              setAdjustmentReason('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleManageTOIL}
              className={adjustmentType === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              disabled={!selectedEmployee || !adjustmentHours || !adjustmentReason}
            >
              {adjustmentType === 'add' ? 'Add' : 'Deduct'} TOIL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TOILDashboard;
